import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './TimePicker.css';

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);

function parseTime(value) {
  if (!value || typeof value !== 'string') {
    return { hours: 12, minutes: 0, empty: true };
  }
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) return { hours: 12, minutes: 0, empty: true };
  const hours = Math.min(23, Math.max(0, parseInt(m[1], 10)));
  let minutes = Math.min(59, Math.max(0, parseInt(m[2], 10)));
  minutes = Math.round(minutes / 5) * 5;
  if (minutes === 60) minutes = 55;
  return { hours, minutes, empty: false };
}

function formatTime(hours, minutes) {
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export default function TimePicker({
  value = '',
  onChange,
  allowEmpty = false,
  placeholder = 'Выберите время',
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 768,
  );
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const parsed = useMemo(() => parseTime(value), [value]);
  const [draftH, setDraftH] = useState(parsed.hours);
  const [draftM, setDraftM] = useState(parsed.minutes);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!open) return;
    setDraftH(parsed.hours);
    setDraftM(parsed.minutes);
  }, [open, parsed.hours, parsed.minutes]);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || isMobile) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const width = 248;
    const height = panelRef.current?.offsetHeight || 260;
    let left = rect.left;
    if (left + width > window.innerWidth - 8) left = window.innerWidth - width - 8;
    left = Math.max(8, left);
    let top = rect.bottom + 6;
    if (top + height > window.innerHeight - 8 && rect.top > height + 12) {
      top = rect.top - height - 6;
    }
    top = Math.max(8, Math.min(top, window.innerHeight - height - 8));
    setPos({ top, left });
  }, [isMobile]);

  useEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const t = window.setTimeout(updatePosition, 0);
    const onWin = () => updatePosition();
    window.addEventListener('scroll', onWin, true);
    window.addEventListener('resize', onWin);
    const prev = document.body.style.overflow;
    if (isMobile) document.body.style.overflow = 'hidden';
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('scroll', onWin, true);
      window.removeEventListener('resize', onWin);
      document.body.style.overflow = prev;
    };
  }, [open, isMobile, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onDown = (e) => {
      if (triggerRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const apply = () => {
    onChange?.(formatTime(draftH, draftM));
    setOpen(false);
  };

  const clear = () => {
    onChange?.('');
    setOpen(false);
  };

  const display = value && !parsed.empty ? formatTime(parsed.hours, parsed.minutes) : '';

  const panel = open
    ? createPortal(
        <>
          <div className="tp-overlay" onClick={() => setOpen(false)} aria-hidden="true" />
          <div
            ref={panelRef}
            className={`tp-panel${isMobile ? ' tp-panel--sheet' : ''}`}
            style={isMobile ? undefined : { top: pos.top, left: pos.left }}
            role="dialog"
            aria-label="Выбор времени"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="tp-preview" aria-live="polite">
              {formatTime(draftH, draftM)}
            </div>

            <div className="tp-section">
              <div className="tp-section-label">Часы</div>
              <div className="tp-hours" role="listbox" aria-label="Часы">
                {HOURS.map((h) => (
                  <button
                    key={h}
                    type="button"
                    role="option"
                    aria-selected={draftH === h}
                    className={`tp-cell${draftH === h ? ' is-active' : ''}`}
                    onClick={() => setDraftH(h)}
                  >
                    {String(h).padStart(2, '0')}
                  </button>
                ))}
              </div>
            </div>

            <div className="tp-section">
              <div className="tp-section-label">Минуты</div>
              <div className="tp-minutes" role="listbox" aria-label="Минуты">
                {MINUTES.map((m) => (
                  <button
                    key={m}
                    type="button"
                    role="option"
                    aria-selected={draftM === m}
                    className={`tp-cell${draftM === m ? ' is-active' : ''}`}
                    onClick={() => setDraftM(m)}
                  >
                    {String(m).padStart(2, '0')}
                  </button>
                ))}
              </div>
            </div>

            <div className="tp-actions">
              {allowEmpty && (
                <button type="button" className="tp-btn tp-btn--ghost" onClick={clear}>
                  Очистить
                </button>
              )}
              <button type="button" className="tp-btn tp-btn--ghost" onClick={() => setOpen(false)}>
                Отмена
              </button>
              <button type="button" className="tp-btn tp-btn--primary" onClick={apply}>
                Готово
              </button>
            </div>
          </div>
        </>,
        document.body,
      )
    : null;

  return (
    <div className={`tp-root${disabled ? ' is-disabled' : ''}`}>
      <button
        ref={triggerRef}
        type="button"
        className={`tp-trigger${display ? '' : ' is-empty'}${open ? ' is-open' : ''}`}
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <polyline points="12 7 12 12 15 14" />
        </svg>
        <span>{display || placeholder}</span>
      </button>
      {panel}
    </div>
  );
}
